import { TagType } from 'jsmediatags/types';
import Track from '../models/track/track';
import { invalidateTrackSearch } from './trackSearch';

/**
 * Pulls the tag-derived fields off a jsmediatags result and onto a Track.
 *
 * Frames reach us in two shapes. jsmediatags normalises nine of them through
 * its SHORTCUTS table, so `tags.title` is a bare value; everything else stays
 * under its raw frame id as `{ id, size, description, data }`, where
 * `description` is the name from the ID3 spec ("BPM (beats per minute)").
 * We read the values and drop the descriptions - they are the same string on
 * every one of 40,000 tracks and derivable from the id.
 *
 * Two shortcuts are not plain values and have caught us out before:
 * `comment` is `{ language, short_description, text }`, and MP4 `trkn`/`disk`
 * are `{ track | disk, total }`.
 */

type Frame = { id: string; size: number; description: string; data: any };

/** A frame is the wrapper object; a shortcut is whatever it holds. */
function isFrame(value: any): value is Frame {
  return (
    value !== null && typeof value === 'object' && typeof value.id === 'string' && 'data' in value
  );
}

/**
 * First of `ids` that is present, unwrapped to its payload. Repeated frames
 * arrive as an array; the first one is the one that counts.
 */
function frameData(tags: any, ...ids: string[]) {
  for (const id of ids) {
    let value = tags[id];

    if (value === undefined || value === null || value === '') continue;

    if (Array.isArray(value)) value = value[0];

    return isFrame(value) ? value.data : value;
  }
}

function tagString(tags: any, ...ids: string[]) {
  const data = frameData(tags, ...ids);

  if (data === undefined || data === null) return undefined;
  // COMM and its MP4 equivalent carry the text next to a language code and a
  // short description. The text is the part worth keeping.
  if (typeof data === 'object') {
    // An empty text with a filled short_description is common on rips, so
    // fall through on blank rather than on absent.
    for (const part of [data.text, data.short_description]) {
      if (typeof part === 'string' && part.trim()) return part.trim();
    }

    return undefined;
  }

  const str = String(data).trim();

  return str || undefined;
}

/**
 * "3/12" -> 3, "07" -> 7, { track: 3, total: 12 } -> 3.
 * Anything that is not a positive whole number is dropped rather than stored
 * as NaN, which would serialise as null and sort ahead of every real value.
 */
function tagNumber(tags: any, key: 'track' | 'disk' | undefined, ...ids: string[]) {
  const data = frameData(tags, ...ids);

  if (data === undefined || data === null) return undefined;

  const raw =
    typeof data === 'object' ? (key && data[key] !== undefined ? data[key] : undefined) : data;

  if (raw === undefined || raw === null) return undefined;

  const parsed = parseInt(String(raw), 10);

  return isNaN(parsed) || parsed <= 0 ? undefined : parsed;
}

/**
 * Copies every field we keep. Returns whether anything actually changed, so
 * an initial add can skip a library write when the tags told us nothing new.
 *
 * Existing values win only when the tag has nothing to offer - a tag read is
 * the more authoritative source for these, and for the ones the constructor
 * already filled the caller has its own precedence rules.
 */
export default function applyTagsToTrack(track: Track, tag: TagType) {
  const tags = tag.tags as any;
  // A field the user set by hand is theirs; the tag does not get it back
  // until the mark is cleared (utilities/trackEdits).
  const keep = track.userEdited || {};

  const before = [
    track.albumArtist,
    track.composer,
    track.comment,
    track.trackNumber,
    track.disc,
    track.bpm,
    track.initialKey,
    track.publisher,
  ].join(String.fromCharCode(31));

  if (!keep.albumArtist)
    track.albumArtist = tagString(tags, 'TPE2', 'TP2', 'aART') || track.albumArtist;
  if (!keep.composer) track.composer = tagString(tags, 'TCOM', 'TCM', '©wrt') || track.composer;
  if (!keep.comment)
    track.comment = tagString(tags, 'comment', 'COMM', 'COM', '©cmt') || track.comment;
  if (!keep.publisher) track.publisher = tagString(tags, 'TPUB', 'TPB') || track.publisher;
  if (!keep.initialKey) track.initialKey = tagString(tags, 'TKEY', 'TKE') || track.initialKey;

  if (!keep.trackNumber)
    track.trackNumber =
      tagNumber(tags, 'track', 'track', 'TRCK', 'TRK', 'trkn') || track.trackNumber;
  if (!keep.disc) track.disc = tagNumber(tags, 'disk', 'TPOS', 'TPA', 'disk') || track.disc;
  if (!keep.bpm) track.bpm = tagNumber(tags, undefined, 'TBPM', 'TBP', 'tmpo') || track.bpm;

  const after = [
    track.albumArtist,
    track.composer,
    track.comment,
    track.trackNumber,
    track.disc,
    track.bpm,
    track.initialKey,
    track.publisher,
  ].join(String.fromCharCode(31));

  if (before !== after) {
    invalidateTrackSearch(track);
    return true;
  }

  return false;
}
